<script setup lang="ts">
/** Step 2 · Check (plan §3 step 2, §6): verify the proposal against the evidence. */
import { computed } from 'vue';
import { acceptNonCritical } from '@/domain';
import Icon from './Icon.vue';
import StepPanel from './StepPanel.vue';
import PanelHead from './PanelHead.vue';
import ValueCard from './ValueCard.vue';
import FamilyCard from './FamilyCard.vue';
import RepairsCard from './RepairsCard.vue';
import { app, snap, ui } from '@/app/state';
import { announce } from '@/app/announce';
import { chooseStyle, proposalFixture, proposalFixtureMatch } from '@/app/analysis';
import { isFitResult } from '@/families/fit-schema';
import { overlayTagIds } from '@/app/overlay';
import { highlightPieces } from '@/app/facts';
import { fieldKeyOf } from '@/app/intake';
import { fieldsToReview, focusSoon, go, labelOf, LABELS, optionalToReview, pendingCritical, visibleFieldKeys } from '@/app/navigation';
import { plural } from '@/app/format';
import { unmodelled } from '@/app/fit-extras';
import { envelopeIssues } from '@/app/fixes';

const manual = computed(() => ui.analysis.status === 'manual');
const keys = computed(() => { void snap.value; return visibleFieldKeys(); });
/** Criticals still to confirm (unresolved ones are "unsure" and do not lock the next steps). */
const pend = computed(() => { void snap.value; return pendingCritical().filter((k) => snap.value.fields[k]!.status !== 'unresolved'); });
const unsure = computed(() => { void snap.value; return fieldsToReview().filter((k) => snap.value.fields[k]!.status === 'unresolved'); });
const optional = computed(() => { void snap.value; return optionalToReview(); });
const title = computed(() => (ui.analysis.status === 'review' ? 'Check the proposal' : manual.value ? 'Check your starting values' : 'Checked values'));
const lede = computed(() => (manual.value ? 'Every value below is a starting point, not evidence. Confirm or change each one.' : 'Compare each value with the photo and the product text. Nothing is confirmed until you say so.'));
const GROUPS: [string, string, string[]][] = [
  ['Size and stock', 'Must be confirmed by you', ['W', 'H', 'D', 'stockThickness']],
  ['Layout', 'Read from the photo', ['bays', 'shelfCount', 'even', 'openings', 'openingRatios']],
  ['Construction', 'Read from the photo', ['back', 'plinth']],
  ['Material', 'From the product text', ['material', 'finish']],
];
const groups = computed(() => GROUPS.map(([t, sub, ks]) => ({ id: `g-${t.replace(/\W/g, '')}`, title: t, sub, keys: ks.filter((k) => keys.value.includes(k)) })).filter((g) => g.keys.length));

const text = computed(() => ui.ref.text);
const pieces = computed(() => {
  const p = ui.analysis.proposal;
  const all = p ? (isFitResult(p) ? p.evidence.textSpans : p.evidence.textSpans) : [];
  const spans = all.filter((s) => text.value.slice(s.span[0], s.span[1]) === s.text);
  return highlightPieces(text.value, spans.map((s) => ({ field: fieldKeyOf(s.kind) ?? s.kind, span: s.span as [number, number], text: s.text })));
});
const showText = computed(() => !manual.value && !!text.value && pieces.value.some((p) => p.field));
/** A recorded fixture answered, not a reading of the user's photo: say so plainly. */
const fixture = computed(() => (manual.value ? null : proposalFixture()));
const fixtureMatch = computed(() => (fixture.value ? proposalFixtureMatch() : null));
/** Sizes outside the construction (FND-02): said in the dock too, so Continue never hides it. */
const outside = computed(() => { void snap.value; return envelopeIssues(); });
const refused = computed(() => ui.analysis.status === 'refused' && !Object.keys(snap.value.fields).length);

const LINES_FOR: Record<string, string[] | 'all'> = { openings: 'all', openingRatios: 'all', shelfCount: 'all', even: 'all', H: ['top', 'bottom'] };
function light(e: Event) {
  const k = (e.target as HTMLElement).closest<HTMLElement>('.vcard')?.dataset.field ?? null;
  ui.litFact = k;
  const ids = overlayTagIds.value;
  const lines = k ? LINES_FOR[k] : undefined;
  ui.litLines = lines === 'all' ? ids : lines ?? [];
}

function acceptOptional() {
  const n = optional.value.length;
  app.store.apply(() => ({ fields: acceptNonCritical(app.store.fields) }), { label: 'Accepted optional values' });
  announce(`${plural(n, 'optional value')} accepted.${pend.value.length ? ' Critical values still need you.' : ''}`);
  focusSoon(pend.value.length ? `[data-confirm="${pend.value[0]}"], [data-use-limit="${pend.value[0]}"]` : '#dock [data-act="continue"]');
}
function onContinue(e: MouseEvent) {
  if ((e.currentTarget as HTMLElement).getAttribute('aria-disabled') === 'true') {
    const first = pend.value[0]!;
    const card = document.querySelector<HTMLElement>(`.vcard[data-field="${first}"]`);
    if (card) {
      card.classList.remove('is-nudge');
      void card.offsetWidth;
      card.classList.add('is-nudge');
      card.scrollIntoView({ block: 'center', behavior: 'smooth' });
      card.querySelector<HTMLElement>('[data-confirm], [data-use-limit]')?.focus({ preventScroll: true });
    }
    announce(`Confirm ${labelOf(first).toLowerCase()} first.`);
    return;
  }
  go('shape');
}
</script>

<template>
  <StepPanel>
    <template v-if="refused">
      <PanelHead :n="2" title="Nothing to check yet" lede="We can’t rebuild the piece in your photo yet, so nothing was measured or guessed. Set it up by hand from the closest style, or use another photo." />
      <div class="row-acts">
        <button type="button" class="btn btn--ink" data-act="manual" @click="chooseStyle">Set up by hand from a style</button>
        <button type="button" class="btn btn--quiet" data-go="reference" @click="go('reference')">Back to Reference</button>
      </div>
    </template>
    <template v-else>
      <PanelHead :n="2" :title="title" :lede="lede" />
      <div v-if="fixture && fixtureMatch === 'photo'" class="summary-bar is-soft" role="note" data-banner="recorded-photo">
        <Icon name="history" />
        <p><strong>Recorded analysis of this photo</strong><span>These values come from an earlier reading of this exact photo, so the demo is quick and repeatable. Check each one as you would a live reading.</span></p>
      </div>
      <div v-else-if="fixture" class="summary-bar is-attn" role="note" data-banner="fixture">
        <Icon name="alert" />
        <p><strong>Sample analysis (recorded) — not a reading of your photo</strong><span>Live photo analysis is offline on this computer, so these values come from a recorded example ({{ fixture }}). Check every one against your own bookcase, or set it up by hand.</span></p>
      </div>
      <FamilyCard v-if="snap.fields.family || ui.analysis.fit" />
      <RepairsCard />
      <p v-if="unmodelled.length" class="calm-note" role="note" data-unmodelled><Icon name="info" /><span>Seen in the photo but not modelled yet: {{ unmodelled.join(', ') }}. The rest is rebuilt; check with your maker.</span></p>
      <div class="summary-bar" :class="pend.length || unsure.length ? 'is-attn' : optional.length ? 'is-soft' : 'is-ok'">
        <template v-if="pend.length"><Icon name="lock" /><p><strong>{{ plural(pend.length, 'critical value') }} to confirm</strong><span>{{ pend.map((k) => LABELS[k]).join(', ') }}</span></p></template>
        <template v-else-if="unsure.length === 1 && unsure[0] === 'repairs'"><Icon name="lock" /><p><strong>The rebuild's changes are held for your review</strong><span>Accept them above, or adjust the layout in Shape, before exporting.</span></p></template>
        <template v-else-if="unsure.length"><Icon name="question" /><p><strong>{{ plural(unsure.length, 'value') }} marked unsure</strong><span>Give {{ unsure.length === 1 ? 'it' : 'them' }} a value or check with your maker.</span></p></template>
        <template v-else-if="optional.length"><Icon name="check" /><p><strong>Critical values confirmed</strong><span>{{ plural(optional.length, 'optional value') }} you can glance at: {{ optional.map(labelOf).join(', ') }}.</span></p></template>
        <template v-else><Icon name="check" /><p><strong>Everything is checked</strong><span>Change anything below at any time.</span></p></template>
        <button v-if="optional.length" type="button" class="btn btn--sm btn--soft" data-act="accept-noncrit" @click="acceptOptional">Accept {{ optional.length }} optional</button>
      </div>
      <details v-if="showText" class="src-text" open>
        <summary>Product text</summary>
        <p class="facts"><template v-for="(p, i) in pieces" :key="i"><mark v-if="p.field" :data-fact="p.field" :class="{ 'is-lit': ui.litFact === p.field }">{{ p.text }}</mark><template v-else>{{ p.text }}</template></template></p>
      </details>
      <div @pointerover="light" @focusin="light">
        <section v-for="g in groups" :key="g.id" class="vgroup" :aria-labelledby="g.id">
          <h2 :id="g.id" class="h-sec">{{ g.title }} <span class="muted">{{ g.sub }}</span></h2>
          <ValueCard v-for="k in g.keys" :key="k" :field="k" />
        </section>
      </div>
    </template>

    <template #dock>
      <p v-if="pend.length" class="dock-note"><Icon name="lock" /> Confirm {{ plural(pend.length, 'more value') }} to continue</p>
      <p v-else-if="outside.length" class="dock-note dock-note--err" data-outside><Icon name="alert" /> {{ outside[0]!.message }}; Shape offers the fix</p>
      <p v-else class="dock-note dock-note--ok"><Icon name="check" /> Ready to shape{{ optional.length ? ` · ${optional.length} optional` : '' }}</p>
      <button type="button" class="btn btn--ink btn--lg" data-act="continue" :aria-disabled="pend.length ? 'true' : undefined" @click="onContinue">Continue to Shape <Icon name="arrow" /></button>
    </template>
  </StepPanel>
</template>
